import {
  BottomSheet,
  BottomSheetContent,
  Button,
  Stack,
  toast,
} from '@relay-towing/ui';
import { Copy, MapPin, MapTrifold } from '@phosphor-icons/react';
import { appleMapsUrl, googleMapsUrl, type MapPlace } from '../../lib/maps';
import { copyText, openExternal } from '../../lib/nativeBridge';

/**
 * What to do with a place on a call: open it in Google Maps or Apple Maps,
 * or copy its address. The pickup's round button opens it. It went
 * straight to Google Maps before, which left a driver who navigates with
 * Apple Maps, or who wanted the address for a text, with nothing.
 */
export function PlaceSheet({
  title,
  place,
  open,
  onOpenChange,
}: {
  /** What the place is: "Pickup location". */
  title: string;
  place: MapPlace;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const leave = (url: string) => {
    openExternal(url);
    onOpenChange(false);
  };

  const copy = async () => {
    const copied = await copyText(place.address);
    onOpenChange(false);
    if (copied) {
      toast.success('Address copied');
      return;
    }
    // The address goes in the message, so it can still be read off and
    // typed where it was needed.
    toast.error('The address could not be copied', {
      description: place.address,
    });
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={title} description={place.address}>
        <Stack gap={4}>
          <Button
            variant="secondary"
            size="xl"
            pill
            fullWidth
            leadingIcon={<MapTrifold />}
            onClick={() => leave(googleMapsUrl(place))}
          >
            Open in Google Maps
          </Button>
          <Button
            variant="secondary"
            size="xl"
            pill
            fullWidth
            leadingIcon={<MapPin />}
            onClick={() => leave(appleMapsUrl(place))}
          >
            Open in Apple Maps
          </Button>
          <Button
            variant="secondary"
            size="xl"
            pill
            fullWidth
            leadingIcon={<Copy />}
            onClick={copy}
          >
            Copy address
          </Button>
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
